import { ArrowUUpLeftIcon, ColumnsIcon, FunnelSimpleIcon } from '@phosphor-icons/react'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PAGE_REMEASURE_EVENT } from '@/components/layout/page'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Pagination, PaginationFilters } from '@/components/ui/pagination'
import { Select } from '@/components/ui/select'
import { getColumnId, Table } from '@/components/ui/table'
import {
  BASE_FILTERS_KEYS,
  NON_RESETABLE_KEYS,
  PAGINATION_KEYS,
  SORT_KEYS,
  useCaptureEvent,
  useLocalStorage,
} from '@/hooks'
import { isEmpty } from '@/utils'

import {
  COLUMN_VISIBILITY_DROPDOWN_WIDTH,
  COLUMN_VISIBILITY_DROPDOWN_WITH_ACTIONS_WIDTH,
} from './table-group.constants'
import * as S from './table-group.styled'
import { TableGroupProps } from './table-group.types'
import { isSortedColumnHidden } from './table-group.utils'

// TODO: TableGroup became a very complex component. It needs to get some performance optimizations.
export const TableGroup = <T, F extends PaginationFilters = PaginationFilters>({
  id,
  columnsStorageKey = id,
  title,
  titleActions,
  navigation,
  filters,
  tableFilters,
  headerFiltersHorizontal,
  enableColumnVisibility = true,
  columns,
  pagination,
  noShadow,
  noHorizontalPadding,
  forceFilteredEmptyState,
  emptyNoFilters: emptyNoFiltersProp,
  emptyWithFilters: emptyWithFiltersProp,
  ...tableProps
}: TableGroupProps<T, F>) => {
  const { t } = useTranslation(['common'])
  const emptyNoFilters = emptyNoFiltersProp ?? {
    description: t('common:table.group.emptyNoFilters.description'),
    title: t('common:table.group.emptyNoFilters.title'),
  }
  const emptyWithFilters = emptyWithFiltersProp ?? {
    description: t('common:table.group.emptyWithFilters.description'),
    title: t('common:table.group.emptyWithFilters.title'),
  }
  const { captureEvent } = useCaptureEvent()

  const cardRef = useRef<HTMLDivElement>(null)
  const titleRowRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  // Note: how much taller Card's min-height needs to be than the table's own 40rem floor, to
  // also fit TitleRow/Header/Pagination (whichever are actually rendered) without squeezing
  // past what they need - see Card's own styled-component comment for why this can't just be a
  // CSS min-height/min-content value. Measured, not guessed: 0 until the first observation
  // fires, which is fine since Card's `flex: 1` still stretches it to fill available space in
  // the meantime, same as it always did.
  const [tableMinHeightExtra, setTableMinHeightExtra] = useState(0)

  // Note: an excludeFromVisibilityControl column (e.g. a trailing delete/approve-decline
  // actions column) has nothing to toggle, so it's excluded from the picker's own option list
  // and from the localStorage-tracked id set entirely - visibleColumns below always includes
  // it regardless.
  const hideableColumns = useMemo(
    () => columns.filter((col) => !col.excludeFromVisibilityControl),
    [columns],
  )

  const initiallyVisibleColumnIds = useMemo(
    () => hideableColumns.filter(({ hiddenByDefault }) => !hiddenByDefault).map(getColumnId),
    [hideableColumns],
  )

  // Note: v2 - storage now keys on column id (see TableColumn.id / getColumnId),
  // not the (possibly translated) title. Bumping the version discards any
  // stale v1 entry instead of leaving it stuck matching nothing - e.g. every
  // column silently vanishing after a language switch, since the stored
  // English title no longer matches any current column.
  // Note: null until the user picks columns - the defaults then follow the current columns
  // instead of freezing the first render's set. Columns can depend on data that loads after
  // mount (e.g. Market Shop Orders' restaurant-only columns, gated on the active building), so a
  // frozen set would hide them on a direct page load but not when navigating in.
  const [storedVisibleColumnIds, setVisibleColumnIds] = useLocalStorage<string[] | null>(
    `table-columns-${columnsStorageKey}`,
    null,
    'v2',
  )
  const visibleColumnIds = storedVisibleColumnIds ?? initiallyVisibleColumnIds
  const [isFiltersVisible, setIsFiltersVisible] = useLocalStorage<boolean>(
    `table-filters-visible-${id}`,
    true,
  )

  const visibleColumns = useMemo(
    () =>
      columns.filter(
        (col) => col.excludeFromVisibilityControl || visibleColumnIds.includes(getColumnId(col)),
      ),
    [columns, visibleColumnIds],
  )

  const columnOptions = hideableColumns.map((col) => ({
    action: col.getVisibilityAction?.({ columns: visibleColumns, data: tableProps.data ?? [] }),
    label: col.title,
    value: getColumnId(col),
  }))

  const hasHiddenColumns = visibleColumnIds.length < hideableColumns.length

  const clearSortIfColumnHidden = (visibleIds: string[]) => {
    if (isSortedColumnHidden(hideableColumns, visibleIds, tableProps.sortBy)) {
      tableProps.onSort?.(null, null)
    }
  }

  const handleColumnVisibilityChange = (selectedIds: string[]) => {
    // Note: Ensure at least one column is always visible
    if (selectedIds.length === 0) return
    setVisibleColumnIds(selectedIds)
    clearSortIfColumnHidden(selectedIds)
    captureEvent('user_action', {
      name: 'table_column_visibility_change',
      screen: id,
      value: selectedIds,
    })
  }

  const handleResetColumns = () => {
    setVisibleColumnIds(null)
    clearSortIfColumnHidden(initiallyVisibleColumnIds)
    captureEvent('user_action', {
      name: 'table_column_visibility_reset',
      screen: id,
      value: initiallyVisibleColumnIds,
    })
  }

  const handleToggleFilters = () => {
    const value = !isFiltersVisible
    setIsFiltersVisible(value)
    captureEvent('user_action', {
      name: 'table_filters_toggle',
      screen: id,
      value,
    })
  }

  // Note: When there are no statusTabs, the space reserved for them would otherwise sit empty -
  // render `filters` there instead of only in the toggleable row below.
  const filtersInNavigation = !navigation?.statusTabs && !!filters
  const hasLeftNavigationContent =
    !!navigation?.statusTabs || filtersInNavigation || !!navigation?.filters

  const hasNavigation =
    navigation?.statusTabs ||
    navigation?.filters ||
    navigation?.datePicker ||
    navigation?.search ||
    navigation?.actions ||
    filtersInNavigation
  const showHeader = hasNavigation || filters || enableColumnVisibility
  const showHeaderActions = hasNavigation || filters || enableColumnVisibility
  const showTitleRow = !!(title || titleActions)

  useLayoutEffect(() => {
    const cardElement = cardRef.current
    if (!cardElement) return

    const measure = () => {
      const paginationElement = cardElement.querySelector<HTMLElement>(
        '[data-component-id="Pagination"]',
      )
      const sections = [titleRowRef.current, headerRef.current, paginationElement].filter(
        (element): element is HTMLElement => !!element,
      )

      const cardStyle = getComputedStyle(cardElement)
      // Note: read Card's own padding/gap from computed style rather than hardcoding
      // ui/card's theme.space['20'] values here - stays correct if that spacing ever changes
      // without this file needing to track it too.
      const paddingTop = Number.parseFloat(cardStyle.paddingTop) || 0
      const paddingBottom = Number.parseFloat(cardStyle.paddingBottom) || 0
      const gap = Number.parseFloat(cardStyle.rowGap || cardStyle.gap) || 0

      const sectionsHeight = sections.reduce(
        (sum, element) => sum + element.getBoundingClientRect().height,
        0,
      )
      // Note: Card lays out TitleRow?/Header?/Table/Pagination? in a column with a gap between
      // every adjacent pair - the table is always present, so there's exactly one gap per extra
      // (non-table) section rendered, regardless of which ones they are.
      const gapsHeight = gap * sections.length

      setTableMinHeightExtra(paddingTop + paddingBottom + sectionsHeight + gapsHeight)
    }

    // Note: the *initial* measurement stays synchronous (not scheduled - see scheduleMeasure
    // below) deliberately - Page's own equivalent effect measures this Card's rendered height
    // as one of its children, and React fires layout effects bottom-up, resolving any state
    // update (and the synchronous re-render it triggers) a child's layout effect makes *before*
    // the parent's own layout effect runs. Deferring this first call to a later frame would
    // have Page's effect read Card's still-uncorrected height instead.
    measure()

    // Note: a rapid, cascading resize (e.g. DevTools' device-toolbar toggle, which resizes many
    // elements across breakpoints in one go) can hit the browser's ResizeObserver loop-limit,
    // silently dropping a notification for one of these elements - since nothing else nudges
    // that element's size afterward, there's no further trigger to correct the stale measurement
    // it left behind, even switching device mode back off. `requestAnimationFrame` defers the
    // *re-measurement* to after the browser has finished the resize's layout pass (rather than
    // mid-cascade).
    let animationFrame: number | null = null
    const scheduleMeasure = () => {
      if (animationFrame !== null) return
      animationFrame = requestAnimationFrame(() => {
        animationFrame = null
        measure()
      })
    }

    // Note: two independent fallback triggers, deliberately calling `measure()` directly rather
    // than `scheduleMeasure` - `requestAnimationFrame` (and, by extension, ResizeObserver's own
    // notification delivery) is suspended entirely while the tab is hidden/backgrounded, which a
    // DevTools device-toolbar toggle can transiently cause. `resize` still fires reliably in that
    // state, and `visibilitychange` firing when the tab becomes visible again is what guarantees
    // this self-corrects once whatever suspended rendering ends - neither depends on rAF/the
    // observer having been able to deliver anything during the hidden window.
    const handleWindowResize = () => measure()
    const handleVisibilityChange = () => {
      if (!document.hidden) measure()
    }

    const observer = new ResizeObserver(scheduleMeasure)
    observer.observe(cardElement)
    if (titleRowRef.current) observer.observe(titleRowRef.current)
    if (headerRef.current) observer.observe(headerRef.current)
    const paginationElement = cardElement.querySelector('[data-component-id="Pagination"]')
    if (paginationElement) observer.observe(paginationElement)

    window.addEventListener('resize', handleWindowResize)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', handleWindowResize)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (animationFrame !== null) cancelAnimationFrame(animationFrame)
    }
    // Note: re-run (re-attaching to the current ref targets) only when a section mounts/unmounts
    // - a section's own size *changing* is already handled by the ResizeObserver callback itself,
    // independent of React's render cycle.
  }, [showTitleRow, showHeader, !!pagination])

  // Note: tells an ancestor Page to re-read this Card's min-height once the new value is
  // committed - Page can't detect a *shrink* on its own (see PAGE_REMEASURE_EVENT). Skips the
  // initial 0, which Page's own mount-time measurement already covers.
  useLayoutEffect(() => {
    if (!tableMinHeightExtra) return
    cardRef.current?.dispatchEvent(new Event(PAGE_REMEASURE_EVENT, { bubbles: true }))
  }, [tableMinHeightExtra])

  // Note: Clears filters that are passed in the expendable filters section
  const handleClearCustomFilters = () => {
    tableFilters?.handleResetFilters({ keepKeys: BASE_FILTERS_KEYS })
    captureEvent('user_action', {
      name: 'table_clear_custom_filters',
      screen: id,
    })
  }

  // Note: Clears all possible filters, except sortBy and sortOrder
  const handleClearAllFilters = () => {
    tableFilters?.handleResetFilters({ keepKeys: SORT_KEYS, resetKeys: NON_RESETABLE_KEYS })
    captureEvent('user_action', {
      name: 'table_clear_all_filters',
      screen: id,
    })
  }

  // Note: Check if there are any active filters - search, date range, status tab, etc.
  const hasActiveAnyFilters = tableFilters
    ? Object.entries(tableFilters.filters).some(([key, value]) => {
        if ([...PAGINATION_KEYS, ...SORT_KEYS].includes(key)) return false

        const initialValue = tableFilters.initialState[key]

        // Note: Without an initial status there is no default tab to compare against (e.g. Clients
        // sets it in an effect), so the status tab can't be told apart from the default.
        if (NON_RESETABLE_KEYS.includes(key) && initialValue == null) return false

        if (Array.isArray(value) && Array.isArray(initialValue)) {
          return JSON.stringify(value) !== JSON.stringify(initialValue)
        }

        return value !== initialValue && !isEmpty(value)
      })
    : false

  const showFilteredEmpty = hasActiveAnyFilters || !!forceFilteredEmptyState

  // Note: Check if there are any fields selected in the expendable filters section
  const hasActiveCustomFilters = tableFilters
    ? Object.entries(tableFilters.filters).some(([key, value]) => {
        if (BASE_FILTERS_KEYS.includes(key)) return false

        const initialValue = tableFilters.initialState[key]

        if (Array.isArray(value) && Array.isArray(initialValue)) {
          return JSON.stringify(value) !== JSON.stringify(initialValue)
        }

        return value !== initialValue && !isEmpty(value)
      })
    : false

  return (
    <S.Card
      ref={cardRef}
      $noHorizontalPadding={noHorizontalPadding}
      $noShadow={noShadow}
      $tableMinHeightExtra={tableMinHeightExtra}
    >
      {showTitleRow && (
        <S.TitleRow ref={titleRowRef}>
          {title}
          {titleActions}
        </S.TitleRow>
      )}
      {showHeader && (
        <S.Header ref={headerRef} $headerFiltersHorizontal={headerFiltersHorizontal}>
          {showHeaderActions && (
            <S.HeaderActions>
              <S.Navigation $hasLeftContent={hasLeftNavigationContent}>
                {navigation?.statusTabs && (
                  <S.NavigationLeft>{navigation.statusTabs}</S.NavigationLeft>
                )}
                {filtersInNavigation && (
                  <S.NavigationLeftFilters>
                    {filters}
                    {tableFilters && hasActiveCustomFilters && (
                      <Button
                        data-clear-filters-button
                        use="ghost"
                        size="small"
                        onClick={handleClearCustomFilters}
                      >
                        <Icon icon={ArrowUUpLeftIcon} />
                        Clear filters
                      </Button>
                    )}
                  </S.NavigationLeftFilters>
                )}
                {navigation?.filters && (
                  <S.NavigationFilters>{navigation.filters}</S.NavigationFilters>
                )}
                {/* Note: grouped with the filter-toggle/column-visibility buttons below so they
                    wrap onto their own row together, instead of the buttons trailing off separately. */}
                {(navigation?.datePicker ||
                  navigation?.search ||
                  navigation?.actions ||
                  (filters && !filtersInNavigation) ||
                  enableColumnVisibility) && (
                  <S.NavigationRight>
                    {navigation?.search && (
                      <S.NavigationSearch>{navigation.search}</S.NavigationSearch>
                    )}
                    {navigation?.datePicker && (
                      <S.NavigationDatePicker>{navigation.datePicker}</S.NavigationDatePicker>
                    )}
                    {(navigation?.actions ||
                      (filters && !filtersInNavigation) ||
                      enableColumnVisibility) && (
                      <S.NavigationIconActions>
                        {navigation?.actions}
                        {filters && !filtersInNavigation && (
                          <S.ActionButtonWrapper>
                            <Button
                              onlyBox
                              active={isFiltersVisible}
                              use="secondary"
                              size="small"
                              title={t('common:table.group.filters')}
                              onClick={handleToggleFilters}
                            >
                              <Icon icon={FunnelSimpleIcon} />
                            </Button>
                          </S.ActionButtonWrapper>
                        )}
                        {enableColumnVisibility && (
                          <S.ActionButtonWrapper>
                            <Select
                              type="multi"
                              options={columnOptions}
                              size="small"
                              value={visibleColumnIds}
                              placement="bottom-end"
                              trigger={
                                <Button
                                  onlyBox
                                  active={hasHiddenColumns}
                                  use="secondary"
                                  size="small"
                                  title={t('common:table.group.columnVisibility')}
                                >
                                  <Icon icon={ColumnsIcon} />
                                </Button>
                              }
                              dropdownOptions={{
                                label: t('common:table.group.chooseColumns'),
                                variant: 'menu',
                                width: columnOptions.some((option) => option.action)
                                  ? COLUMN_VISIBILITY_DROPDOWN_WITH_ACTIONS_WIDTH
                                  : COLUMN_VISIBILITY_DROPDOWN_WIDTH,
                              }}
                              sortSelectedFirst={false}
                              onChange={handleColumnVisibilityChange}
                              onReset={handleResetColumns}
                            />
                          </S.ActionButtonWrapper>
                        )}
                      </S.NavigationIconActions>
                    )}
                  </S.NavigationRight>
                )}
              </S.Navigation>
            </S.HeaderActions>
          )}
          {filters && !filtersInNavigation && isFiltersVisible && (
            <S.FiltersWrapper>
              {filters}
              {tableFilters && hasActiveCustomFilters && (
                <Button
                  data-clear-filters-button
                  use="ghost"
                  size="small"
                  onClick={handleClearCustomFilters}
                >
                  <Icon icon={ArrowUUpLeftIcon} />
                  Clear filters
                </Button>
              )}
            </S.FiltersWrapper>
          )}
        </S.Header>
      )}
      <Table
        id={id}
        columns={visibleColumns}
        emptyText={showFilteredEmpty ? emptyWithFilters.title : emptyNoFilters.title}
        emptyDescription={
          showFilteredEmpty ? emptyWithFilters.description : emptyNoFilters.description
        }
        onClearFilters={showFilteredEmpty ? handleClearAllFilters : undefined}
        {...tableProps}
      />
      {pagination && <Pagination {...pagination} />}
    </S.Card>
  )
}
